// Host wiring for Help & Learning (owner: Claude; the tutorial module itself is Codex's).
//
// Signed out: the tutorial keeps progress in this browser only, as before.
// Signed in: progress belongs to the signed-in person. It is loaded from and saved to
// the server (ptw_learning_progress_get/_save) and follows the person to any device.
// Account administrators also get a read-only overview of everyone's progress on their
// sites. None of this is a training record or competence, and nothing in the permit
// lifecycle reads it.
import { useEffect, useId, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ErrorBanner } from '@/components/evidence'
import { Badge, Banner, Button, Card, EmptyState, Field, PageHeader, Select, Spinner, type Tone } from '@/components/ui'
import type { LearningProgressRecord, LearningProgressState } from '@/domain/types'
import { roleLabel } from '@/features/home/permitList'
import { LearningPage, type LearningPageProps, tutorialContent } from '@/features/learning'
import { fmtDateTime, relativeTo } from '@/lib/format'
import { useLearningOverview, useLearningProgress, useMe, useSession } from '@/lib/queries'
import { getSnapshot } from '@/lib/session'
import { createAccountStorage, type SyncState } from './accountProgress'
import { callLearningRpc } from './learningRpc'
import {
  applyOverview,
  FINISHED_MEANS,
  type LearningStatus,
  NO_ROLE,
  type OverviewFilter,
  type OverviewSort,
  overviewCsv,
  roleOptions,
  STATUS_LABEL,
} from './overviewModel'

export { createAccountStorage, personProgressKey, saveModeFor } from './accountProgress'
export { overviewRow, progressSentence } from './overviewModel'

const CONTENT_ID = tutorialContent.id
const CONTENT_VERSION = tutorialContent.version

export const isAccountAdmin = (caps: readonly string[] | undefined): boolean => Boolean(caps?.includes('administer_accounts'))

/** Guided practice for a signed-in person, with progress kept on that person's record. */
export function AccountLearning(props: Omit<LearningPageProps, 'progressStorage' | 'progressScope'>) {
  const session = useSession()
  const cacheIdentity = session.identityKey ?? 'none'
  const me = useMe()
  const saved = useLearningProgress(CONTENT_ID, CONTENT_VERSION)

  if (me.isPending || saved.isPending) {
    return (
      <div className="flex justify-center p-8" role="status" aria-label="Loading your learning progress">
        <Spinner />
      </div>
    )
  }
  if (!me.data) {
    // Without the person, fall back to this browser: learning must never be blocked.
    return (
      <>
        <div className="mb-4">
          <Banner tone="warn" title="Progress is kept in this browser for now">
            Your sign-in could not be checked, so reading and practice are saved on this device only.
          </Banner>
        </div>
        <LearningPage {...props} />
      </>
    )
  }
  return (
    <SignedInLearning
      key={`${cacheIdentity}:${me.data.person.id}`}
      personId={me.data.person.id}
      cacheIdentity={cacheIdentity}
      serverProgress={saved.data?.progress ?? null}
      loadFailed={saved.isError}
      admin={isAccountAdmin(me.data.global_capabilities)}
      pageProps={props}
    />
  )
}

function SignedInLearning({ personId, cacheIdentity, serverProgress, loadFailed, admin, pageProps }: {
  personId: string
  cacheIdentity: string
  serverProgress: LearningProgressState | null
  loadFailed: boolean
  admin: boolean
  pageProps: Omit<LearningPageProps, 'progressStorage' | 'progressScope'>
}) {
  const queryClient = useQueryClient()
  const [sync, setSync] = useState<SyncState>('idle')
  const [storage] = useState(() =>
    createAccountStorage({
      content: tutorialContent,
      personId,
      serverProgress,
      // A delayed flush/retry belongs to the originating learner, never whichever
      // person happens to be signed in after this component has unmounted.
      canSave: () => {
        const current = getSnapshot()
        return current.identityKey !== null && current.identityKey === cacheIdentity
      },
      save: async (progress, mode) => {
        const saved = await callLearningRpc<LearningProgressRecord>(cacheIdentity, 'ptw_learning_progress_save', {
          p_content_id: CONTENT_ID,
          p_content_version: CONTENT_VERSION,
          p_progress: progress,
          p_mode: mode,
        })
        // Update the captured person's query, even if the response arrives after
        // navigation. Home and a remounted practice view must see the saved result.
        queryClient.setQueryData<LearningProgressRecord>(
          ['learning-progress', cacheIdentity, CONTENT_ID, CONTENT_VERSION],
          previous => ({ ...previous, ...saved }),
        )
      },
      onState: setSync,
    }),
  )
  // Send a pending change when the person leaves: tab hidden, page closed or route left.
  // If that request does not get out, the change is resent the next time /learn opens.
  useEffect(() => {
    const flush = () => void storage.flush()
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pagehide', flush)
      document.removeEventListener('visibilitychange', onVisibility)
      flush()
    }
  }, [storage])
  return (
    <>
      {(admin || loadFailed || sync === 'failed') && (
        <div className="mb-4 space-y-2" data-testid="learning-host-notes">
          {admin && (
            <p className="text-sm">
              <Link to="/learn/progress" className="font-semibold text-brand underline" data-testid="open-learning-overview">
                See everyone&apos;s learning progress
              </Link>
            </p>
          )}
          {loadFailed && (
            <Banner tone="warn" title="Your saved progress could not be loaded">
              You can keep learning. What you do now is kept in this browser and saved to your record when the connection
              returns.
            </Banner>
          )}
          {sync === 'failed' && !loadFailed && (
            <Banner tone="warn" title="Not saved to your record yet">
              Your progress is kept in this browser and will be saved to your record when the connection returns.
            </Banner>
          )}
        </div>
      )}
      <LearningPage {...pageProps} progressStorage={storage} progressScope="account" />
    </>
  )
}

const STATUS_TONE: Record<LearningStatus, Tone> = { not_started: 'neutral', started: 'info', finished: 'ok', older_only: 'warn' }

/** /learn/progress: read-only overview for account administrators. */
export function LearningProgressPage() {
  const me = useMe()
  const admin = isAccountAdmin(me.data?.global_capabilities)
  const q = useLearningOverview(CONTENT_ID, CONTENT_VERSION, admin)
  const [filter, setFilter] = useState<OverviewFilter>({ role: '', status: '' })
  const [sort, setSort] = useState<OverviewSort>('activity')
  const ids = { role: useId(), status: useId(), sort: useId() }

  if (me.isPending || (admin && q.isPending)) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    )
  }
  if (!admin) {
    return (
      <div className="max-w-3xl">
        <PageHeader title="Learning progress" />
        <Banner tone="info" title="Only an account administrator can see this page">
          Your own progress is on the <Link to="/learn" className="underline">Help &amp; Learning</Link> page.
        </Banner>
      </div>
    )
  }
  if (!q.data) return <ErrorBanner error={q.error ?? new Error('Learning progress is not available.')} title="Could not load learning progress" />

  const data = q.data
  const all = applyOverview(data.people, { role: '', status: '' }, 'activity')
  const rows = applyOverview(data.people, filter, sort)
  const count = (s: LearningStatus) => all.filter((x) => x.r.status === s).length
  const roles = roleOptions(data.people)
  const filtered = Boolean(filter.role || filter.status)

  function download() {
    const filterNote = filtered
      ? `Filtered: ${[filter.role && `role ${filter.role === NO_ROLE ? 'none' : filter.role}`, filter.status && STATUS_LABEL[filter.status]].filter(Boolean).join(', ')}; ${rows.length} of ${all.length} people.`
      : `All ${all.length} people on the administrator's sites.`
    const csv = overviewCsv(rows, {
      contentVersion: data.content_version,
      generatedAt: data.generated_at,
      exportedBy: me.data?.person.display_name ?? 'an account administrator',
      synthetic: Boolean(me.data?.is_synthetic),
      filterNote,
    }, fmtDateTime)
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `learning-self-study-progress-${data.generated_at.slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Learning progress"
        subtitle={`Help & Learning self-study, content ${CONTENT_VERSION}. ${count('started') + count('finished')} of ${all.length} people have started; ${count('finished')} finished.`}
        actions={<Link to="/learn" className="tap inline-flex items-center text-sm font-semibold text-brand underline">Back to Help &amp; Learning</Link>}
      />
      <Banner tone="info" title="Self-study only">
        This shows what each person has read and practised in the training copy. It is not a training record, an assessment or
        proof of competence, and it never grants a role, licence or authority to work. The content is a draft pending HSE review.
      </Banner>
      {count('older_only') > 0 ? (
        <Banner tone="warn" title={`${count('older_only')} ${count('older_only') === 1 ? 'person needs' : 'people need'} the new edition`}>
          They worked through an earlier edition of the tutorial. A new edition starts everyone again, so their earlier progress
          does not count for {CONTENT_VERSION}.
        </Banner>
      ) : null}

      <Card>
        <div className="grid gap-x-4 sm:grid-cols-3" role="group" aria-label="Filter and sort">
          <Field label="Role" htmlFor={ids.role}>
            <Select
              id={ids.role}
              data-testid="filter-role"
              value={filter.role}
              onChange={(e) => setFilter({ ...filter, role: e.target.value })}
              placeholder="All roles"
              options={roles.map((code) => ({ value: code, label: code === NO_ROLE ? 'No PTW role' : roleLabel(code) }))}
            />
          </Field>
          <Field label="Status" htmlFor={ids.status}>
            <Select
              id={ids.status}
              data-testid="filter-status"
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value as LearningStatus | '' })}
              placeholder="Any status"
              options={(Object.keys(STATUS_LABEL) as LearningStatus[]).map((s) => ({ value: s, label: `${STATUS_LABEL[s]} (${count(s)})` }))}
            />
          </Field>
          <Field label="Sort by" htmlFor={ids.sort}>
            <Select
              id={ids.sort}
              data-testid="sort"
              value={sort}
              onChange={(e) => setSort(e.target.value as OverviewSort)}
              options={[
                { value: 'activity', label: 'Last activity' },
                { value: 'name', label: 'Name' },
                { value: 'progress', label: 'Most progress' },
              ]}
            />
          </Field>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ink-soft" aria-live="polite" data-testid="overview-count">
            Showing {rows.length} of {all.length} {all.length === 1 ? 'person' : 'people'}. {FINISHED_MEANS}
          </p>
          <Button variant="secondary" onClick={download} disabled={rows.length === 0} data-testid="export-csv">
            Download as spreadsheet (CSV)
          </Button>
        </div>
      </Card>

      {all.length === 0 ? (
        <EmptyState title="Nobody on your sites has signed in yet">People appear here after they first sign in.</EmptyState>
      ) : rows.length === 0 ? (
        <EmptyState title="Nobody matches these filters">Change the role or status filter to see more people.</EmptyState>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" aria-label="People and their learning progress" data-testid="learning-overview">
          {rows.map(({ p, r }) => (
            <li key={p.person_id}>
              <Card className="h-full">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold break-words">{p.display_name}</p>
                    <p className="text-sm text-ink-soft">
                      {[r.personaRole, p.organisation].filter(Boolean).join(' · ')}
                    </p>
                    {r.roles.length > 0 ? <p className="text-[13px] text-ink-faint">PTW roles: {r.roles.join(', ')}</p> : null}
                  </div>
                  <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                </div>
                {r.started ? (
                  <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                    <div className="col-span-2">
                      <dt className="text-ink-soft">Path</dt>
                      <dd>{r.pathLabel}</dd>
                    </div>
                    <Meter label="Steps read" value={r.read} total={r.pathLength} />
                    <Meter label="Actions practised" value={r.practised} total={r.pathLength} />
                    <div className="col-span-2">
                      <dt className="text-ink-soft">Current step</dt>
                      <dd>{r.current ?? 'Not chosen'}</dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-ink-soft">Last activity</dt>
                      <dd title={fmtDateTime(p.updated_at)}>
                        {fmtDateTime(p.updated_at)} ({relativeTo(p.updated_at, data.generated_at)})
                      </dd>
                    </div>
                  </dl>
                ) : (
                  <p className="mt-3 text-sm text-ink-soft">Has not opened this edition of the walkthrough yet.</p>
                )}
                {p.older_edition ? (
                  <p className="mt-3 rounded-lg border border-warn/30 bg-warn-light px-3 py-2 text-[13px] leading-normal text-ink" data-testid="older-edition">
                    Earlier edition {p.older_edition.content_version}: {p.older_edition.read_count} steps read,{' '}
                    {p.older_edition.practised_count} practised, last on {fmtDateTime(p.older_edition.updated_at)}.
                    {r.started ? '' : ' Needs to go through the new edition.'}
                  </p>
                ) : null}
              </Card>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-ink-soft">Updated {fmtDateTime(data.generated_at)}. Refreshes every 30 seconds.</p>
    </div>
  )
}

function Meter({ label, value, total }: { label: string; value: number; total: number }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0
  return (
    <div>
      <dt className="text-ink-soft">{label}</dt>
      <dd>
        <span className="font-semibold">{value}</span> of {total}
        <span className="mt-1 block h-1.5 rounded-full bg-line" aria-hidden="true">
          <span className="block h-1.5 rounded-full bg-brand" style={{ width: `${pct}%` }} />
        </span>
      </dd>
    </div>
  )
}
